import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { OnboardingContext } from '../contexts/OnboardingContext';
import OnboardGuard from '../components/OnboardGuard';
import Logo from '../components/common/Logo';
import Footer from '../components/common/Footer';
import OnboardingProgressBar from '../components/common/OnboardingProgressBar';
import { Sparkles, Check, X, Loader2, DollarSign, Calendar, ChevronDown, Edit2 } from 'lucide-react';
import { Button } from '../components/ui';

function ReviewDetectedInner() {
  const navigate = useNavigate();
  const { setOnboardingStepIfNotComplete } = useContext(OnboardingContext);

  const [phase, setPhase] = useState('syncing'); // 'syncing' | 'reviewing' | 'completing'
  const [suggestions, setSuggestions] = useState([]);
  const [processingId, setProcessingId] = useState(null);
  const [error, setError] = useState('');
  const [acceptedCount, setAcceptedCount] = useState(0);
  const [syncPhaseText, setSyncPhaseText] = useState('Connecting to your bank...');

  // New state for UX enhancements
  const [expandedTransactions, setExpandedTransactions] = useState({});
  const [loadingTransactions, setLoadingTransactions] = useState(null);
  const [editingName, setEditingName] = useState(null); // { suggestionId, name }
  const [selectedTypes, setSelectedTypes] = useState({}); // { suggestionId: 'bill' | 'debt' | 'income' }
  const [editingAmount, setEditingAmount] = useState(null); // { suggestionId, amount }

  useEffect(() => {
    runInitialSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize selectedTypes when suggestions load
  useEffect(() => {
    if (suggestions.length > 0) {
      const initialTypes = {};
      suggestions.forEach(s => {
        const suggestedType = s.suggested_item_type || s.suggestedItemType || s.item_type || s.itemType || 'bill';
        initialTypes[s.id] = suggestedType;
      });
      setSelectedTypes(initialTypes);
    }
  }, [suggestions]);

  async function runInitialSync() {
    try {
      setPhase('syncing');
      setError('');
      setSyncPhaseText('Syncing your recent transactions...');

      // Backfill: 365 days to detect quarterly/annual recurring items (MIN_OCCURRENCES = 2 requires ~180+ days for quarterly)
      await api.post('/api/planner/backfill', { force: true, skipAI: true, days: 365 });

      setSyncPhaseText('Analyzing transaction patterns...');
      const res = await api.get('/api/planner/recurring-suggestions?runAIMatch=false');

      if (res.data.success) {
        const pending = (res.data.suggestions || []).filter(s => s.status === 'pending');
        setSuggestions(pending);
        setSyncPhaseText(`Found ${pending.length} recurring item${pending.length !== 1 ? 's' : ''}!`);
      }

      // Brief delay so user sees the "Found X items!" message
      await new Promise(r => setTimeout(r, 800));
      setPhase('reviewing');
    } catch (err) {
      console.error('Initial sync failed:', err);
      setError('Failed to scan your transactions. You can skip to the dashboard and set up later.');
      setPhase('reviewing');
    }
  }

  async function handleAccept(id, nameOverride) {
    try {
      setProcessingId(id);
      const s = suggestions.find(item => item.id === id);
      const nameToUse = nameOverride || s?.normalized_name || s?.normalizedName || s?.merchant_name;
      const typeToUse = selectedTypes[id] || s?.suggested_item_type || s?.suggestedItemType || 'bill';

      // Use edited amount if available, otherwise use detected amount
      const amountToUse = editingAmount?.suggestionId === id
        ? parseFloat(editingAmount.amount)
        : parseFloat(s?.typical_amount || s?.typicalAmount || 0);

      await api.post(`/api/planner/recurring-suggestions/${id}/accept`, {
        name: nameToUse,
        item_type: typeToUse,
        amount: amountToUse,
      });
      setSuggestions(prev => prev.filter(item => item.id !== id));
      setAcceptedCount(prev => prev + 1);
      setEditingAmount(null);
    } catch (err) {
      console.error('Accept failed:', err);
      setError('Failed to accept item. Please try again.');
    } finally {
      setProcessingId(null);
    }
  }

  async function handleDismiss(id) {
    try {
      setProcessingId(id);
      await api.post(`/api/planner/recurring-suggestions/${id}/dismiss`, {
        reason: 'not_recurring',
      });
      setSuggestions(prev => prev.filter(item => item.id !== id));
    } catch (err) {
      console.error('Dismiss failed:', err);
      setError('Failed to dismiss item. Please try again.');
    } finally {
      setProcessingId(null);
    }
  }

  // Helper: Format frequency for display
  function formatFrequency(frequency) {
    const map = {
      'weekly': 'Weekly',
      'biweekly': 'Biweekly',
      'monthly': 'Monthly',
      'quarterly': 'Quarterly',
      'yearly': 'Yearly'
    };
    return map[frequency] || frequency || 'Unknown';
  }

  // Helper: Toggle transaction expansion
  async function toggleTransactions(suggestionId) {
    if (expandedTransactions[suggestionId]) {
      // Collapse
      const next = { ...expandedTransactions };
      delete next[suggestionId];
      setExpandedTransactions(next);
      return;
    }

    // Load transactions
    try {
      setLoadingTransactions(suggestionId);
      const res = await api.get(`/api/planner/recurring-suggestions/${suggestionId}/transactions`);
      if (res.data.success) {
        setExpandedTransactions(prev => ({
          ...prev,
          [suggestionId]: res.data.transactions
        }));
      }
    } catch (err) {
      console.error('Failed to load transactions:', err);
      setError('Failed to load transaction history. Please try again.');
    } finally {
      setLoadingTransactions(null);
    }
  }

  // Helper: Start editing name
  function startEditingName(suggestionId, currentName) {
    setEditingName({ suggestionId, name: currentName });
  }

  // Helper: Cancel editing
  function cancelEditingName() {
    setEditingName(null);
  }

  // Helper: Accept with edited name
  async function handleAcceptWithEdit(id) {
    const nameToUse = editingName?.suggestionId === id
      ? editingName.name
      : suggestions.find(s => s.id === id)?.normalized_name;

    await handleAccept(id, nameToUse);
    setEditingName(null);
    setEditingAmount(null);
  }

  // Helper: Update selected type
  function handleTypeChange(suggestionId, newType) {
    setSelectedTypes(prev => ({
      ...prev,
      [suggestionId]: newType
    }));
  }

  // Helper: Start editing amount
  function startEditingAmount(suggestionId, currentAmount) {
    setEditingAmount({ suggestionId, amount: currentAmount.toFixed(2) });
  }

  // Helper: Cancel amount editing
  function cancelEditingAmount() {
    setEditingAmount(null);
  }

  // Helper: Calculate next occurrence date for display
  function calculateNextOccurrence(frequency, dayOfWeek, dayOfMonth, lastOccurrence) {
    if (!frequency) return null;

    const today = new Date();
    const last = lastOccurrence ? new Date(lastOccurrence + 'T12:00:00') : today;
    let next = new Date(last);

    switch (frequency.toLowerCase()) {
      case 'weekly':
        // Add 7 days from last occurrence
        next.setDate(next.getDate() + 7);
        // If in the past, keep adding weeks until future
        while (next <= today) {
          next.setDate(next.getDate() + 7);
        }
        break;

      case 'biweekly':
        // Add 14 days from last occurrence
        next.setDate(next.getDate() + 14);
        while (next <= today) {
          next.setDate(next.getDate() + 14);
        }
        break;

      case 'monthly':
        // Next month, same day
        next.setMonth(next.getMonth() + 1);
        if (dayOfMonth) {
          next.setDate(Math.min(dayOfMonth, new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()));
        }
        while (next <= today) {
          next.setMonth(next.getMonth() + 1);
          if (dayOfMonth) {
            next.setDate(Math.min(dayOfMonth, new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()));
          }
        }
        break;

      case 'quarterly':
        // Add 3 months
        next.setMonth(next.getMonth() + 3);
        if (dayOfMonth) {
          next.setDate(Math.min(dayOfMonth, new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()));
        }
        while (next <= today) {
          next.setMonth(next.getMonth() + 3);
          if (dayOfMonth) {
            next.setDate(Math.min(dayOfMonth, new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()));
          }
        }
        break;

      case 'yearly':
        // Add 1 year
        next.setFullYear(next.getFullYear() + 1);
        if (dayOfMonth) {
          next.setDate(Math.min(dayOfMonth, new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()));
        }
        while (next <= today) {
          next.setFullYear(next.getFullYear() + 1);
          if (dayOfMonth) {
            next.setDate(Math.min(dayOfMonth, new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()));
          }
        }
        break;

      default:
        return null;
    }

    return next;
  }

  // Helper: Format next occurrence for display
  function formatNextOccurrence(frequency, dayOfWeek, dayOfMonth, lastOccurrence) {
    const next = calculateNextOccurrence(frequency, dayOfWeek, dayOfMonth, lastOccurrence);
    if (!next) return null;

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const dayName = dayNames[next.getDay()];
    const monthName = monthNames[next.getMonth()];
    const date = next.getDate();

    return `${dayName}, ${monthName} ${date}`;
  }

  async function handleContinue() {
    try {
      setPhase('completing');
      await api.post('/api/user/onboarding', { step: 'complete' });
      setOnboardingStepIfNotComplete('complete');
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to complete onboarding:', err);
      setError('Failed to complete setup. Please try again.');
      setPhase('reviewing');
    }
  }

  // --- SYNCING PHASE ---
  if (phase === 'syncing') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
        <div className="w-full py-6 px-4 border-b border-gray-200 bg-white">
          <div className="max-w-4xl mx-auto flex justify-center">
            <Logo size="sm" />
          </div>
        </div>

        {/* Progress bar */}
        <OnboardingProgressBar currentStep="review_detected" />

        <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
          <div className="w-full max-w-md text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="bg-purple-100 p-4 rounded-full">
                <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              Scanning Your Transactions
            </h1>
            <p className="text-gray-600 text-base sm:text-lg mb-8">
              {syncPhaseText}
            </p>
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
                <p className="text-base sm:text-sm text-gray-500">This usually takes 30–60 seconds</p>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  // --- REVIEWING / COMPLETING PHASE ---
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
      {/* Header */}
      <div className="w-full py-6 px-4 border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto flex justify-center">
          <Logo size="sm" />
        </div>
      </div>

      {/* Progress bar */}
      <OnboardingProgressBar currentStep="review_detected" />

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-3xl">
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-purple-100 p-3 rounded-full">
                <Sparkles className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 text-center">
              {suggestions.length > 0
                ? 'We Found Your Recurring Items'
                : acceptedCount > 0
                ? 'All Set!'
                : 'Ready to Set Up Your Bills'}
            </h1>
            <p className="text-gray-600 text-center text-base sm:text-lg">
              {suggestions.length > 0
                ? 'Review the bills and income we detected. Accept the ones you want to track in your forecast.'
                : acceptedCount > 0
                ? 'Great job! You can always add more items later from Bills & Income.'
                : 'No recurring patterns detected in your recent transactions. You can add bills and income manually from your dashboard.'}
            </p>
          </div>

          {error && (
            <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 text-base sm:text-sm text-red-800">
              <p>{error}</p>
            </div>
          )}

          {/* Accepted count badge */}
          {acceptedCount > 0 && (
            <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-3 text-base sm:text-sm text-green-800 text-center">
              {acceptedCount} item{acceptedCount !== 1 ? 's' : ''} added to your forecast
            </div>
          )}

          {/* Suggestion cards */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-6 mb-6">
            {suggestions.length > 0 ? (
              <ul className="space-y-3">
                {suggestions.map(s => {
                  const name = s.normalized_name || s.normalizedName || 'Unknown';
                  const amount = parseFloat(s.typical_amount || s.typicalAmount) || 0;
                  const count = s.occurrence_count || s.occurrenceCount || 0;
                  const suggestedType = s.suggested_item_type || s.suggestedItemType || s.item_type || s.itemType || 'bill';
                  const currentType = selectedTypes[s.id] || suggestedType;
                  const accountName = s.account_name || '';
                  const isProcessing = processingId === s.id;

                  return (
                    <li
                      key={s.id}
                      className="p-3 sm:p-4 bg-gray-50 rounded-lg space-y-3"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                        {/* Icon */}
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                          currentType === 'income' ? 'bg-green-100' : currentType === 'debt' ? 'bg-orange-100' : 'bg-blue-100'
                        }`}>
                          <DollarSign className={`w-5 h-5 ${
                            currentType === 'income' ? 'text-green-600' : currentType === 'debt' ? 'text-orange-600' : 'text-blue-600'
                          }`} />
                        </div>

                        {/* Main info */}
                        <div className="flex-1 min-w-0">
                          {/* Name with inline edit */}
                          {editingName?.suggestionId === s.id ? (
                            <div className="flex items-center gap-2 mb-1">
                              <input
                                type="text"
                                value={editingName.name}
                                onChange={(e) => setEditingName({ ...editingName, name: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleAcceptWithEdit(s.id);
                                  if (e.key === 'Escape') cancelEditingName();
                                }}
                                autoFocus
                                className="flex-1 px-2 py-2 text-base sm:text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter name..."
                              />
                              <button
                                onClick={() => handleAcceptWithEdit(s.id)}
                                className="min-w-[44px] min-h-[44px] flex items-center justify-center text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                              >
                                ✓
                              </button>
                              <button
                                onClick={cancelEditingName}
                                className="min-w-[44px] min-h-[44px] flex items-center justify-center text-sm bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 mb-1">
                              <div className="font-medium text-gray-900 truncate">{name}</div>
                              <button
                                onClick={() => startEditingName(s.id, name)}
                                className="text-gray-400 hover:text-blue-600 flex-shrink-0 p-2 -m-1"
                                title="Edit name"
                                aria-label="Edit name"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}

                          {/* Amount (editable) + Frequency badges */}
                          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 mt-0.5">
                            {/* Amount with inline edit */}
                            {editingAmount?.suggestionId === s.id ? (
                              <div className="flex items-center gap-1">
                                <span className="font-semibold">$</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={editingAmount.amount}
                                  onChange={(e) => setEditingAmount({ ...editingAmount, amount: e.target.value })}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      setEditingAmount(null);
                                    }
                                    if (e.key === 'Escape') cancelEditingAmount();
                                  }}
                                  autoFocus
                                  className="w-28 px-2 py-2 text-base sm:text-sm font-semibold border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <span className="font-semibold">${amount.toFixed(2)}</span>
                                <button
                                  onClick={() => startEditingAmount(s.id, amount)}
                                  className="text-gray-400 hover:text-blue-600 p-2 -m-1"
                                  title="Edit amount"
                                  aria-label="Edit amount"
                                >
                                  <Edit2 className="w-4 h-4 sm:w-3 sm:h-3" />
                                </button>
                              </div>
                            )}

                            {/* Frequency badge - toned down */}
                            {s.detected_frequency && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                                {formatFrequency(s.detected_frequency)}
                              </span>
                            )}

                            {/* Expandable occurrences */}
                            <button
                              onClick={() => toggleTransactions(s.id)}
                              className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm sm:text-xs min-h-[44px] sm:min-h-0"
                            >
                              {loadingTransactions === s.id ? (
                                <>
                                  <div className="w-3 h-3 border border-blue-600 border-t-transparent rounded-full animate-spin" />
                                  Loading...
                                </>
                              ) : (
                                <>
                                  <Calendar className="w-3.5 h-3.5" />
                                  {count} occurrence{count !== 1 ? 's' : ''}
                                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${
                                    expandedTransactions[s.id] ? 'rotate-180' : ''
                                  }`} />
                                </>
                              )}
                            </button>
                          </div>

                          {/* Account name */}
                          {accountName && (
                            <div className="text-xs text-gray-400 mt-0.5 truncate">{accountName}</div>
                          )}

                          {/* Next occurrence - read-only */}
                          {(() => {
                            const nextDate = formatNextOccurrence(
                              s.detected_frequency,
                              s.detected_day_of_week || s.detectedDayOfWeek,
                              s.detected_day_of_month || s.detectedDayOfMonth,
                              s.last_occurrence || s.lastOccurrence
                            );
                            return nextDate ? (
                              <div className="text-xs text-gray-500 mt-1">
                                Next: {nextDate}
                              </div>
                            ) : null;
                          })()}

                          {/* Type selector - Outlined buttons (no label) */}
                          <div className="flex items-center gap-2 mt-2">
                            {suggestedType === 'income' ? (
                              // Income items - subtle text badge
                              <span className="text-xs text-gray-500">
                                Income
                              </span>
                            ) : (
                              // Expense items - Outlined Bill or Debt buttons
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleTypeChange(s.id, 'bill')}
                                  className={`px-4 py-2 sm:px-3 sm:py-1 rounded-full text-sm sm:text-xs font-medium transition-all border min-h-[44px] sm:min-h-0 ${
                                    currentType === 'bill'
                                      ? 'border-blue-500 text-blue-600 bg-blue-50'
                                      : 'border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-600'
                                  }`}
                                >
                                  Bill
                                </button>
                                <button
                                  onClick={() => handleTypeChange(s.id, 'debt')}
                                  className={`px-4 py-2 sm:px-3 sm:py-1 rounded-full text-sm sm:text-xs font-medium transition-all border min-h-[44px] sm:min-h-0 ${
                                    currentType === 'debt'
                                      ? 'border-orange-500 text-orange-600 bg-orange-50'
                                      : 'border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-600'
                                  }`}
                                >
                                  Debt
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        </div>
                        {/* Action buttons */}
                        <div className="flex items-center gap-2 justify-end mt-2 pt-2 border-t border-gray-100 sm:mt-0 sm:pt-0 sm:border-t-0 flex-shrink-0">
                          <button
                            onClick={() => editingName?.suggestionId === s.id ? handleAcceptWithEdit(s.id) : handleAccept(s.id)}
                            disabled={isProcessing}
                            title="Add to forecast"
                            className="p-2 rounded-lg bg-green-100 text-green-700 hover:bg-green-200 transition-colors disabled:opacity-50 min-h-[44px] min-w-[44px] flex items-center justify-center"
                          >
                            {isProcessing ? (
                              <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                              <Check className="w-5 h-5" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDismiss(s.id)}
                            disabled={isProcessing}
                            title="Skip"
                            className="p-2 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors disabled:opacity-50 min-h-[44px] min-w-[44px] flex items-center justify-center"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      {/* Expanded transactions */}
                      {expandedTransactions[s.id] && (
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <div className="text-xs font-medium text-gray-500 mb-2">Transaction History</div>
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {expandedTransactions[s.id].map((txn, idx) => (
                              <div
                                key={txn.id || idx}
                                className="flex items-center justify-between text-sm gap-3 py-1"
                              >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <span className="text-gray-500 text-xs w-20 flex-shrink-0">
                                    {new Date(txn.date).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: '2-digit'
                                    })}
                                  </span>
                                  <span className="text-gray-700 truncate text-xs">
                                    {txn.merchant_name || txn.name}
                                  </span>
                                </div>
                                <span className="font-medium text-gray-900 text-xs flex-shrink-0">
                                  ${Math.abs(parseFloat(txn.amount)).toFixed(2)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                {acceptedCount > 0 ? (
                  <>
                    <p className="font-medium text-gray-700">All items reviewed</p>
                    <p className="text-base sm:text-sm mt-1">You're all set! Continue to your dashboard.</p>
                  </>
                ) : (
                  <>
                    <p className="font-medium text-gray-700">No recurring bills detected yet</p>
                    <p className="text-base sm:text-sm mt-1">
                      You can add bills and income manually from the Bills & Income page.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Continue button */}
          <Button
            onClick={handleContinue}
            variant="primary"
            fullWidth
            disabled={phase === 'completing'}
            loading={phase === 'completing'}
          >
            {phase === 'completing' ? 'Setting up...' : 'Continue to Dashboard'}
          </Button>

          <p className="text-center text-base sm:text-sm text-gray-500 mt-4">
            You can always add, edit, or remove items later from Bills & Income.
          </p>
          <div className="text-center mt-2">
            <button
              onClick={handleContinue}
              className="text-base sm:text-sm text-gray-400 hover:text-gray-600 underline transition-colors min-h-[44px] sm:min-h-0 inline-flex items-center"
            >
              Skip to Dashboard — I'll set up bills later
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default function ReviewDetected() {
  return (
    <OnboardGuard currentStep="review_detected">
      <ReviewDetectedInner />
    </OnboardGuard>
  );
}
